<template>
  <div>
    <Header />

    <!-- Hero Section -->
    <section class="relative text-white pt-16 h-80 flex items-center">
      <!-- Background -->
      <div class="absolute inset-0 hero-gradient"></div>
      <div class="absolute inset-0 bg-black bg-opacity-30"></div>

      <!-- Content -->
      <div
        class="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center"
      >
        <div class="flex items-center justify-center mb-4">
          <div class="bg-white bg-opacity-20 p-4 rounded-full mr-4">
            <svg
              class="w-8 h-8 text-white"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
              ></path>
            </svg>
          </div>
          <h1 class="text-4xl md:text-5xl font-bold animate-fade-in-up">
            Politique de Confidentialité
          </h1>
        </div>
        <p
          class="text-xl md:text-2xl opacity-90 max-w-3xl mx-auto animate-fade-in-up"
        >
          Protection et respect de vos données personnelles
        </p>
      </div>
    </section>

    <!-- Breadcrumb -->
    <section class="bg-white py-4 border-b">
      <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <nav class="flex" aria-label="Breadcrumb">
          <ol class="flex items-center space-x-4">
            <li>
              <div>
                <a href="/" class="text-gray-400 hover:text-gray-500">
                  <svg
                    class="flex-shrink-0 h-5 w-5"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                  >
                    <path
                      d="M10.707 2.293a1 1 0 00-1.414 0l-7 7a1 1 0 001.414 1.414L4 10.414V17a1 1 0 001 1h2a1 1 0 001-1v-2a1 1 0 011-1h2a1 1 0 011 1v2a1 1 0 001 1h2a1 1 0 001-1v-6.586l.293.293a1 1 0 001.414-1.414l-7-7z"
                    />
                  </svg>
                  <span class="sr-only">Accueil</span>
                </a>
              </div>
            </li>
            <li>
              <div class="flex items-center">
                <svg
                  class="flex-shrink-0 h-5 w-5 text-gray-300"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path
                    fill-rule="evenodd"
                    d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z"
                    clip-rule="evenodd"
                  />
                </svg>
                <span class="ml-4 text-sm font-medium text-gray-500"
                  >Politique de Confidentialité</span
                >
              </div>
            </li>
          </ol>
        </nav>
      </div>
    </section>

    <!-- Introduction -->
    <section class="py-16 bg-white">
      <div class="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div
          class="bg-gradient-to-r from-slate-50 to-gray-50 rounded-2xl p-8 mb-12"
        >
          <div class="flex items-start space-x-4">
            <div class="bg-slate-600 p-3 rounded-full">
              <svg
                class="w-6 h-6 text-white"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                ></path>
              </svg>
            </div>
            <div>
              <h2 class="text-2xl font-bold text-gray-900 mb-4">
                Notre Engagement
              </h2>
              <p class="text-gray-600 leading-relaxed mb-4">
                L'Association des Médecins Généralistes d'Oran (AMGO) s'engage à
                protéger et respecter votre vie privée. Cette politique de
                confidentialité explique comment nous collectons, utilisons et
                protégeons vos informations personnelles.
              </p>
              <p class="text-sm text-gray-500">
                <strong>Dernière mise à jour :</strong> 15 janvier 2024
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Sections principales -->
    <section class="py-16 bg-gray-50">
      <div class="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div class="space-y-8">
          <!-- 1. Collecte des données -->
          <div class="bg-white rounded-xl shadow-lg p-8 section-card">
            <div class="flex items-start space-x-4 mb-6">
              <div class="bg-blue-100 p-3 rounded-full">
                <svg
                  class="w-6 h-6 text-blue-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                  ></path>
                </svg>
              </div>
              <div>
                <h3 class="text-2xl font-bold text-gray-900 mb-4">
                  1. Collecte des Données
                </h3>
              </div>
            </div>

            <div class="space-y-6">
              <div>
                <h4 class="text-lg font-semibold text-gray-900 mb-3">
                  Données que nous collectons :
                </h4>
                <ul class="space-y-2">
                  <li class="flex items-start space-x-3">
                    <span class="w-2 h-2 bg-blue-500 rounded-full mt-2"></span>
                    <span class="text-gray-600"
                      ><strong>Informations d'identification :</strong> Nom,
                      prénom, titre professionnel</span
                    >
                  </li>
                  <li class="flex items-start space-x-3">
                    <span class="w-2 h-2 bg-blue-500 rounded-full mt-2"></span>
                    <span class="text-gray-600"
                      ><strong>Coordonnées :</strong> Adresse email, numéro de
                      téléphone</span
                    >
                  </li>
                  <li class="flex items-start space-x-3">
                    <span class="w-2 h-2 bg-blue-500 rounded-full mt-2"></span>
                    <span class="text-gray-600"
                      ><strong>Informations professionnelles :</strong>
                      Spécialité, lieu d'exercice, numéro d'ordre</span
                    >
                  </li>
                  <li class="flex items-start space-x-3">
                    <span class="w-2 h-2 bg-blue-500 rounded-full mt-2"></span>
                    <span class="text-gray-600"
                      ><strong>Données de navigation :</strong> Adresse IP, type
                      de navigateur, pages visitées</span
                    >
                  </li>
                </ul>
              </div>

              <div class="bg-blue-50 p-4 rounded-lg">
                <h4 class="text-lg font-semibold text-gray-900 mb-2">
                  Comment nous collectons ces données :
                </h4>
                <ul class="space-y-1 text-sm text-gray-600">
                  <li>• Formulaires d'adhésion et de contact</li>
                  <li>• Inscription aux événements et formations</li>
                  <li>• Cookies et technologies similaires</li>
                  <li>• Communications directes avec notre association</li>
                </ul>
              </div>
            </div>
          </div>

          <!-- 2. Utilisation des données -->
          <div class="bg-white rounded-xl shadow-lg p-8 section-card">
            <div class="flex items-start space-x-4 mb-6">
              <div class="bg-green-100 p-3 rounded-full">
                <svg
                  class="w-6 h-6 text-green-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"
                  ></path>
                </svg>
              </div>
              <div>
                <h3 class="text-2xl font-bold text-gray-900 mb-4">
                  2. Utilisation des Données
                </h3>
              </div>
            </div>

            <div class="space-y-6">
              <div>
                <h4 class="text-lg font-semibold text-gray-900 mb-3">
                  Nous utilisons vos données pour :
                </h4>
                <div class="grid md:grid-cols-2 gap-4">
                  <div class="bg-green-50 p-4 rounded-lg">
                    <h5 class="font-semibold text-gray-900 mb-2">
                      🎯 Services principaux
                    </h5>
                    <ul class="space-y-1 text-sm text-gray-600">
                      <li>• Gestion des adhésions</li>
                      <li>• Organisation d'événements</li>
                      <li>• Formation continue</li>
                      <li>• Support et assistance</li>
                    </ul>
                  </div>
                  <div class="bg-green-50 p-4 rounded-lg">
                    <h5 class="font-semibold text-gray-900 mb-2">
                      📧 Communications
                    </h5>
                    <ul class="space-y-1 text-sm text-gray-600">
                      <li>• Newsletters scientifiques</li>
                      <li>• Invitations aux événements</li>
                      <li>• Informations importantes</li>
                      <li>• Réponses à vos demandes</li>
                    </ul>
                  </div>
                </div>
              </div>

              <div class="bg-amber-50 border border-amber-200 p-4 rounded-lg">
                <div class="flex items-center mb-2">
                  <svg
                    class="w-5 h-5 text-amber-600 mr-2"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z"
                    ></path>
                  </svg>
                  <h5 class="font-semibold text-amber-800">Base légale</h5>
                </div>
                <p class="text-sm text-amber-700">
                  Nous traitons vos données sur la base de votre consentement,
                  de l'exécution d'un contrat, ou de notre intérêt légitime à
                  développer nos activités associatives.
                </p>
              </div>
            </div>
          </div>

          <!-- 3. Protection des données -->
          <div class="bg-white rounded-xl shadow-lg p-8 section-card">
            <div class="flex items-start space-x-4 mb-6">
              <div class="bg-purple-100 p-3 rounded-full">
                <svg
                  class="w-6 h-6 text-purple-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"
                  ></path>
                </svg>
              </div>
              <div>
                <h3 class="text-2xl font-bold text-gray-900 mb-4">
                  3. Protection et Sécurité
                </h3>
              </div>
            </div>

            <div class="space-y-6">
              <div>
                <h4 class="text-lg font-semibold text-gray-900 mb-3">
                  Mesures de sécurité mises en place :
                </h4>
                <div class="grid md:grid-cols-3 gap-4">
                  <div class="text-center p-4 bg-purple-50 rounded-lg">
                    <div
                      class="bg-purple-600 w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-3"
                    >
                      <svg
                        class="w-6 h-6 text-white"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          stroke-linecap="round"
                          stroke-linejoin="round"
                          stroke-width="2"
                          d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                        ></path>
                      </svg>
                    </div>
                    <h5 class="font-semibold text-gray-900 mb-2">
                      Chiffrement
                    </h5>
                    <p class="text-sm text-gray-600">
                      Données chiffrées en transit et au repos
                    </p>
                  </div>
                  <div class="text-center p-4 bg-purple-50 rounded-lg">
                    <div
                      class="bg-purple-600 w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-3"
                    >
                      <svg
                        class="w-6 h-6 text-white"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          stroke-linecap="round"
                          stroke-linejoin="round"
                          stroke-width="2"
                          d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
                        ></path>
                      </svg>
                    </div>
                    <h5 class="font-semibold text-gray-900 mb-2">
                      Accès Limité
                    </h5>
                    <p class="text-sm text-gray-600">
                      Accès restreint aux personnes autorisées
                    </p>
                  </div>
                  <div class="text-center p-4 bg-purple-50 rounded-lg">
                    <div
                      class="bg-purple-600 w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-3"
                    >
                      <svg
                        class="w-6 h-6 text-white"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          stroke-linecap="round"
                          stroke-linejoin="round"
                          stroke-width="2"
                          d="M4 7v10c0 2.21 3.582 4 8 4s8-1.79 8-4V7M4 7c0 2.21 3.582 4 8 4s8-1.79 8-4M4 7c0-2.21 3.582-4 8-4s8 1.79 8 4"
                        ></path>
                      </svg>
                    </div>
                    <h5 class="font-semibold text-gray-900 mb-2">Sauvegarde</h5>
                    <p class="text-sm text-gray-600">
                      Sauvegardes régulières et sécurisées
                    </p>
                  </div>
                </div>
              </div>

              <div class="bg-red-50 border border-red-200 p-4 rounded-lg">
                <h4 class="text-lg font-semibold text-red-800 mb-2">
                  🚫 Ce que nous ne faisons JAMAIS :
                </h4>
                <ul class="space-y-1 text-sm text-red-700">
                  <li>• Vendre ou louer vos données à des tiers</li>
                  <li>• Utiliser vos données à des fins commerciales</li>
                  <li>• Partager vos informations sans votre consentement</li>
                  <li>• Conserver vos données plus longtemps que nécessaire</li>
                </ul>
              </div>
            </div>
          </div>

          <!-- 4. Vos droits -->
          <div class="bg-white rounded-xl shadow-lg p-8 section-card">
            <div class="flex items-start space-x-4 mb-6">
              <div class="bg-indigo-100 p-3 rounded-full">
                <svg
                  class="w-6 h-6 text-indigo-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                  ></path>
                </svg>
              </div>
              <div>
                <h3 class="text-2xl font-bold text-gray-900 mb-4">
                  4. Vos Droits
                </h3>
              </div>
            </div>

            <div class="space-y-6">
              <p class="text-gray-600">
                Conformément à la réglementation en vigueur, vous disposez des
                droits suivants concernant vos données personnelles :
              </p>

              <div class="grid md:grid-cols-2 gap-6">
                <div class="space-y-4">
                  <div class="flex items-start space-x-3">
                    <div
                      class="bg-indigo-600 w-6 h-6 rounded-full flex items-center justify-center mt-1"
                    >
                      <span class="text-white text-xs font-bold">1</span>
                    </div>
                    <div>
                      <h5 class="font-semibold text-gray-900">Droit d'accès</h5>
                      <p class="text-sm text-gray-600">
                        Obtenir une copie de vos données personnelles
                      </p>
                    </div>
                  </div>

                  <div class="flex items-start space-x-3">
                    <div
                      class="bg-indigo-600 w-6 h-6 rounded-full flex items-center justify-center mt-1"
                    >
                      <span class="text-white text-xs font-bold">2</span>
                    </div>
                    <div>
                      <h5 class="font-semibold text-gray-900">
                        Droit de rectification
                      </h5>
                      <p class="text-sm text-gray-600">
                        Corriger ou mettre à jour vos informations
                      </p>
                    </div>
                  </div>

                  <div class="flex items-start space-x-3">
                    <div
                      class="bg-indigo-600 w-6 h-6 rounded-full flex items-center justify-center mt-1"
                    >
                      <span class="text-white text-xs font-bold">3</span>
                    </div>
                    <div>
                      <h5 class="font-semibold text-gray-900">
                        Droit à l'effacement
                      </h5>
                      <p class="text-sm text-gray-600">
                        Demander la suppression de vos données
                      </p>
                    </div>
                  </div>
                </div>

                <div class="space-y-4">
                  <div class="flex items-start space-x-3">
                    <div
                      class="bg-indigo-600 w-6 h-6 rounded-full flex items-center justify-center mt-1"
                    >
                      <span class="text-white text-xs font-bold">4</span>
                    </div>
                    <div>
                      <h5 class="font-semibold text-gray-900">
                        Droit d'opposition
                      </h5>
                      <p class="text-sm text-gray-600">
                        Vous opposer au traitement de vos données
                      </p>
                    </div>
                  </div>

                  <div class="flex items-start space-x-3">
                    <div
                      class="bg-indigo-600 w-6 h-6 rounded-full flex items-center justify-center mt-1"
                    >
                      <span class="text-white text-xs font-bold">5</span>
                    </div>
                    <div>
                      <h5 class="font-semibold text-gray-900">
                        Droit à la portabilité
                      </h5>
                      <p class="text-sm text-gray-600">
                        Récupérer vos données dans un format lisible
                      </p>
                    </div>
                  </div>

                  <div class="flex items-start space-x-3">
                    <div
                      class="bg-indigo-600 w-6 h-6 rounded-full flex items-center justify-center mt-1"
                    >
                      <span class="text-white text-xs font-bold">6</span>
                    </div>
                    <div>
                      <h5 class="font-semibold text-gray-900">
                        Droit de limitation
                      </h5>
                      <p class="text-sm text-gray-600">
                        Limiter le traitement de vos données
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              <div class="bg-indigo-50 p-6 rounded-lg">
                <h4 class="text-lg font-semibold text-gray-900 mb-3">
                  Comment exercer vos droits ?
                </h4>
                <p class="text-gray-600 mb-4">
                  Pour exercer l'un de ces droits, contactez-nous par email à
                  l'adresse ci-dessous. Nous vous répondrons dans un délai
                  maximum de 30 jours.
                </p>
                <div class="flex items-center space-x-4">
                  <a
                    href="mailto:contact@amgoran.dz"
                    class="bg-indigo-600 text-white px-6 py-2 rounded-lg font-semibold hover:bg-indigo-700 transition-colors"
                  >
                    📧 contact@amgoran.dz
                  </a>
                  <span class="text-sm text-gray-500">ou</span>
                  <span class="text-sm text-gray-600">📞 0671 15 32 16</span>
                </div>
              </div>
            </div>
          </div>

          <!-- 5. Cookies -->
          <div class="bg-white rounded-xl shadow-lg p-8 section-card">
            <div class="flex items-start space-x-4 mb-6">
              <div class="bg-orange-100 p-3 rounded-full">
                <svg
                  class="w-6 h-6 text-orange-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"
                  ></path>
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                  ></path>
                </svg>
              </div>
              <div>
                <h3 class="text-2xl font-bold text-gray-900 mb-4">
                  5. Cookies et Technologies Similaires
                </h3>
              </div>
            </div>

            <div class="space-y-6">
              <div>
                <h4 class="text-lg font-semibold text-gray-900 mb-3">
                  Types de cookies utilisés :
                </h4>
                <div class="space-y-4">
                  <div class="border border-gray-200 rounded-lg p-4">
                    <div class="flex items-center mb-2">
                      <span
                        class="bg-green-100 text-green-800 px-2 py-1 rounded-full text-xs font-semibold mr-3"
                        >NÉCESSAIRES</span
                      >
                      <h5 class="font-semibold text-gray-900">
                        Cookies techniques
                      </h5>
                    </div>
                    <p class="text-sm text-gray-600">
                      Indispensables au fonctionnement du site (navigation,
                      sécurité, préférences de langue)
                    </p>
                  </div>

                  <div class="border border-gray-200 rounded-lg p-4">
                    <div class="flex items-center mb-2">
                      <span
                        class="bg-blue-100 text-blue-800 px-2 py-1 rounded-full text-xs font-semibold mr-3"
                        >FONCTIONNELS</span
                      >
                      <h5 class="font-semibold text-gray-900">
                        Cookies de préférences
                      </h5>
                    </div>
                    <p class="text-sm text-gray-600">
                      Mémorisent vos choix et préférences pour améliorer votre
                      expérience
                    </p>
                  </div>

                  <div class="border border-gray-200 rounded-lg p-4">
                    <div class="flex items-center mb-2">
                      <span
                        class="bg-purple-100 text-purple-800 px-2 py-1 rounded-full text-xs font-semibold mr-3"
                        >ANALYTIQUES</span
                      >
                      <h5 class="font-semibold text-gray-900">
                        Cookies statistiques
                      </h5>
                    </div>
                    <p class="text-sm text-gray-600">
                      Nous aident à comprendre comment vous utilisez notre site
                      (données anonymisées)
                    </p>
                  </div>
                </div>
              </div>

              <div class="bg-orange-50 p-4 rounded-lg">
                <h4 class="text-lg font-semibold text-gray-900 mb-2">
                  Gestion des cookies
                </h4>
                <p class="text-sm text-gray-600 mb-3">
                  Vous pouvez contrôler et gérer les cookies via les paramètres
                  de votre navigateur :
                </p>
                <div class="grid md:grid-cols-3 gap-4 text-sm">
                  <div class="text-center">
                    <div class="font-semibold text-gray-900">Chrome</div>
                    <div class="text-gray-600">
                      Paramètres > Confidentialité
                    </div>
                  </div>
                  <div class="text-center">
                    <div class="font-semibold text-gray-900">Firefox</div>
                    <div class="text-gray-600">Options > Vie privée</div>
                  </div>
                  <div class="text-center">
                    <div class="font-semibold text-gray-900">Safari</div>
                    <div class="text-gray-600">
                      Préférences > Confidentialité
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- 6. Contact et réclamations -->
          <div class="bg-white rounded-xl shadow-lg p-8 section-card">
            <div class="flex items-start space-x-4 mb-6">
              <div class="bg-red-100 p-3 rounded-full">
                <svg
                  class="w-6 h-6 text-red-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                  ></path>
                </svg>
              </div>
              <div>
                <h3 class="text-2xl font-bold text-gray-900 mb-4">
                  6. Contact et Réclamations
                </h3>
              </div>
            </div>

            <div class="space-y-6">
              <div class="grid md:grid-cols-2 gap-6">
                <div class="bg-gray-50 p-6 rounded-lg">
                  <h4 class="text-lg font-semibold text-gray-900 mb-4">
                    Délégué à la Protection des Données
                  </h4>
                  <div class="space-y-2 text-sm text-gray-600">
                    <p><strong>Email :</strong> dpo@amgoran.dz</p>
                    <p><strong>Téléphone :</strong> 0671 15 32 16</p>
                    <p>
                      <strong>Adresse :</strong><br />
                      AMGO - Service DPO<br />
                      31, Rue 1er Novembre<br />
                      Bir El Djir - Oran<br />
                      BR52-RP-31000 - Algérie
                    </p>
                  </div>
                </div>

                <div class="bg-gray-50 p-6 rounded-lg">
                  <h4 class="text-lg font-semibold text-gray-900 mb-4">
                    Autorité de Contrôle
                  </h4>
                  <p class="text-sm text-gray-600 mb-3">
                    Si vous estimez que vos droits ne sont pas respectés, vous
                    pouvez introduire une réclamation auprès de l'autorité de
                    protection des données compétente.
                  </p>
                  <div class="text-sm text-gray-600">
                    <p><strong>Délai de réponse :</strong> 30 jours maximum</p>
                    <p><strong>Recours :</strong> Gratuit</p>
                  </div>
                </div>
              </div>

            
            </div>
          </div>
        </div>
      </div>
    </section>

    <Footer />
  </div>
</template>

<script>
import Header from "../components/Header.vue";
import Footer from "../components/Footer.vue";
export default {
  name: "Confidentialites",
  components: {
    Header,
    Footer,
  },
  data() {
    return {};
  },
};
</script>
<style scoped>
.hero-gradient {
  background: linear-gradient(135deg, #4f6bb6 0%, #5a67a8 100%);
}
.smooth-scroll {
  scroll-behavior: smooth;
}

@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.animate-fade-in-up {
  animation: fadeInUp 1s ease-out;
}

.section-card {
  transition: transform 0.3s ease, box-shadow 0.3s ease;
}

.section-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 10px 25px -5px rgba(0, 0, 0, 0.1);
}
</style>
